import {
  MouseEvent as ReactMouseEvent,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ChevronLeft, ChevronRight, Loader2, ZoomIn, ZoomOut } from "lucide-react";

// The house pdf.js pattern — same legacy build and pinned worker as the lease review pane and the
// chatbot corpus viewer. Keep the worker version in lockstep with the installed pdfjs-dist.
const PDFJS_WORKER_SRC =
  "https://cdn.jsdelivr.net/npm/pdfjs-dist@3.11.174/legacy/build/pdf.worker.min.js";

async function loadPdfjs() {
  const pdfjs = await import(/* webpackChunkName: "pdfjs" */ "pdfjs-dist/legacy/build/pdf");
  if (!pdfjs.GlobalWorkerOptions.workerSrc) {
    pdfjs.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_SRC;
  }
  return pdfjs;
}

/** A highlight box, in fractions of the page so it survives any render width. */
interface Rect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** One page's place in the stack, in CSS pixels. */
interface PageSlot {
  /** Natural size at scale 1, as the PDF declares it. */
  baseWidth: number;
  baseHeight: number;
  width: number;
  height: number;
  /** Distance from the top of the scroll content. */
  top: number;
}

/** Gap between stacked pages, matching the CSS. */
const PAGE_GAP = 16;

/**
 * How far beyond the viewport a page is still drawn.
 *
 * Generous enough that an ordinary scroll never reaches an undrawn page, tight enough that a
 * thirty-sheet pack holds three or four canvases rather than thirty. A drawing sheet is roughly 5 MB
 * of bitmap at pane width on a 2x display, so the difference is 20 MB against 150 MB.
 */
const RENDER_MARGIN = 700;

/** How far in and out the reader can go. 4x is enough to read a dimension on a 34x22 sheet. */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 4;

interface PdfPaneProps {
  file: File | null;
  /**
   * The document is on its way but not here yet.
   *
   * Without this, a null file means two completely different things — "nothing has been chosen" and
   * "we are fetching 27 MB of PDF" — and the pane said "No document open" for both. A reviewer
   * arriving at a section was told there was nothing to see, for several seconds, while there was.
   */
  loading?: boolean;
  /** 1-based page to show. */
  page: number;
  /** Text to mark on that page, if the reader quoted any. */
  quote?: string;
  /** Changes whenever the reviewer moves, so re-opening the same row jumps again. */
  jumpKey: number;
  /**
   * Also search the page's annotations for the quote, not only its text layer.
   *
   * OFF BY DEFAULT, AND ESSENTIAL FOR DRAWINGS. On a CAD-plotted sheet most of the text is not in
   * the text layer at all: AutoCAD SHX fonts plot as vector outlines and the plot driver captures
   * each string as a `Square` annotation carrying the real text. A text-layer search on such a sheet
   * finds roughly a tenth of what is written on it, so a highlight would silently never appear.
   *
   * The cost of looking is one extra call per page, so this is a flag rather than always-on only
   * because a 272-page standard has no annotations worth walking.
   */
  searchAnnotations?: boolean;
  /**
   * Stack every page and scroll them, instead of showing one at a time.
   *
   * OFF BY DEFAULT, AND THAT DEFAULT IS FOR STANDARDS. A 272-page standard cannot be stacked: the
   * placeholders alone are a mile of scroll, and reading one end to end is not what that screen is
   * for — checking six values against the pages they came from is. A thirty-sheet drawing pack is
   * the opposite case. A reviewer wants to look through it.
   *
   * Only the pages near the viewport are ever drawn, so the cost is the same either way; what
   * changes is whether the scrollbar represents the document or a single page.
   */
  continuous?: boolean;
  /** What to say while the caller is still fetching the file. */
  loadingLabel?: string;
  /**
   * How much of the file has arrived, 0-1, where the caller can measure it.
   *
   * Null means unmeasurable, and then it stays a spinner. A bar that has to invent its denominator
   * tells the reader something the code does not know.
   */
  loadingProgress?: number | null;
}

/**
 * The document, beside the fields taken from it.
 *
 * Two modes, and the difference is only what the scrollbar means. Paged shows one page and steps
 * between them; continuous stacks the whole document and scrolls it. Both draw ONLY the pages near
 * the viewport — a drawing sheet is about 5 MB of bitmap at pane width, so thirty live canvases
 * would be 150 MB to show one sheet.
 *
 * The highlight is worked out here rather than sent by the server. pdf.js already hands us every text
 * run with its position when the page renders, so finding the quote is a local search — and a quote
 * that cannot be found on the page it claims simply does not get marked, which is the same honesty the
 * span-verification gate applies on the way in.
 */
const PdfPane = ({
  file,
  loading = false,
  page,
  quote,
  jumpKey,
  searchAnnotations = false,
  continuous = false,
  loadingLabel = "Opening the document…",
  loadingProgress = null,
}: PdfPaneProps) => {
  const [pageCount, setPageCount] = useState(0);
  const [current, setCurrent] = useState(page);
  /** The marks, and the page they belong to — in a stack they are not always on screen. */
  const [marks, setMarks] = useState<{ page: number; rects: Rect[] }>({ page: 0, rects: [] });
  const [rendering, setRendering] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** Natural page sizes, read once per document. Empty until the document is open. */
  const [baseSizes, setBaseSizes] = useState<{ width: number; height: number }[]>([]);
  const [paneWidth, setPaneWidth] = useState(0);
  /** 1 is fit-to-pane. */
  const [zoom, setZoom] = useState(1);

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const canvasesRef = useRef<Map<number, HTMLCanvasElement>>(new Map());
  /**
   * Which canvas each page has been drawn into.
   *
   * The canvas, not a bare "yes" — that is what makes the check survive React handing the same DOM
   * node back through a new ref callback, while still redrawing a page whose canvas genuinely was
   * replaced.
   */
  const drawnRef = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const docRef = useRef<any>(null);
  // Guards against an out-of-order render finishing after a newer one.
  const tokenRef = useRef(0);
  /** The last jump actually performed, so a relayout does not replay it. */
  const jumpedRef = useRef("");
  /** The render in flight for a canvas, and a counter that tells a stale one it has been superseded. */
  const tasksRef = useRef<Map<HTMLCanvasElement, any>>(new Map());
  const generationRef = useRef<Map<HTMLCanvasElement, number>>(new Map());
  /** One promise chain per canvas, so two renders can never overlap on it. */
  const queuesRef = useRef<Map<HTMLCanvasElement, Promise<void>>>(new Map());
  /** Where to put the scroll once a zoom has relaid the pages out. */
  const anchorRef = useRef<{ top: number; left: number } | null>(null);
  /** The grab in progress, if the reader is dragging the document about. */
  const dragRef = useRef<{ x: number; y: number; left: number; top: number } | null>(null);

  // Open the document once per file.
  useEffect(() => {
    if (!file) {
      docRef.current = null;
      setPageCount(0);
      setBaseSizes([]);
      return;
    }

    let cancelled = false;
    setError(null);
    drawnRef.current.clear();
    canvasesRef.current.clear();

    (async () => {
      try {
        const pdfjs = await loadPdfjs();
        const bytes = new Uint8Array(await file.arrayBuffer());
        // pdf.js takes ownership of the buffer it is handed, so it gets a copy.
        const doc = await pdfjs.getDocument({ data: bytes.slice(), isEvalSupported: false }).promise; // CVE-2024-4367 mitigation
        if (cancelled) return;
        docRef.current = doc;
        setPageCount(doc.numPages);

        // The stack needs every page's shape before it can lay anything out, or the scrollbar would
        // grow as pages drew and the document would squirm under the reader. Reading a page's
        // viewport does not render it. Paged mode never asks, so a 272-page standard never pays.
        //
        // MEASURED, AND IT COSTS 7 ms FOR THIRTY SHEETS. Worth stating, because this loop LOOKS
        // like the expensive part of opening a document and is not: on the reference pack the parse
        // above takes 419 ms and the download before it takes everything else. Publishing sheet 1's
        // size early so it could draw while the rest were measured was tried and removed — it moved
        // seven milliseconds and bought a loading state nobody could read.
        if (continuous) {
          const sizes: { width: number; height: number }[] = [];
          for (let n = 1; n <= doc.numPages; n++) {
            const viewport = (await doc.getPage(n)).getViewport({ scale: 1 });
            if (cancelled) return;
            sizes.push({ width: viewport.width, height: viewport.height });
          }
          setBaseSizes(sizes);
        }
      } catch (err) {
        if (cancelled) return;
        console.error("[verity] pdf render failed:", err);
        setError("This file could not be opened as a PDF.");
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [file, continuous]);

  /** The pane's usable width, and whatever it becomes when the window changes. */
  useLayoutEffect(() => {
    const measure = () => {
      const wrap = wrapRef.current;
      if (wrap) setPaneWidth(Math.max(320, wrap.clientWidth - 32));
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [pageCount, continuous]);

  /**
   * The width a page is actually drawn at: the pane, times however far in the reader has zoomed.
   *
   * Zoom is a RE-RENDER, not a CSS transform. Scaling the canvas up in CSS would enlarge the pixels
   * it already has, and the whole reason to zoom into a drawing is to read something that is not
   * legible at fit-width — which needs pixels that were never drawn. pdf.js redraws from the vector
   * source, so type stays sharp at 400%.
   */
  const renderWidth = paneWidth * zoom;

  /** Where every page sits in the stack. */
  const slots = useMemo<PageSlot[]>(() => {
    if (!continuous || renderWidth === 0) return [];
    let top = 0;
    return baseSizes.map(({ width, height }) => {
      const scale = renderWidth / width;
      const slot: PageSlot = {
        baseWidth: width,
        baseHeight: height,
        width: renderWidth,
        height: Math.round(height * scale),
        top,
      };
      top += slot.height + PAGE_GAP;
      return slot;
    });
  }, [continuous, baseSizes, renderWidth]);

  /**
   * Stops whatever is drawing into a canvas, and waits until it has actually stopped.
   *
   * KEYED ON THE CANVAS, NOT THE PAGE. pdf.js refuses two concurrent renders into one canvas, and
   * the two modes collide in different ways: paged reuses ONE canvas for every page, so page 2 can
   * start while page 1 is still drawing; the stack gives each page its own canvas, but releasing a
   * page mid-render and scrolling back starts a second render into the same one. The canvas is the
   * thing being contended, so it is the thing to key on.
   */
  /**
   * Claims a canvas for a new piece of work, and tells whatever holds it to stop.
   *
   * Both halves are SYNCHRONOUS on purpose. The previous design checked for an in-flight render and
   * awaited its cancellation, which looks careful and is not: between a caller clearing that check
   * and reaching `render()` there is no await, so a second caller could look for a task to cancel,
   * find none because the first had not registered yet, and go on to start its own render into the
   * same canvas. Claiming cannot be something a caller waits for.
   */
  const claim = useCallback((canvas: HTMLCanvasElement): number => {
    const generation = (generationRef.current.get(canvas) ?? 0) + 1;
    generationRef.current.set(canvas, generation);
    const task = tasksRef.current.get(canvas);
    if (task) {
      tasksRef.current.delete(canvas);
      // Not awaited — the queue below does the waiting, and the cancellation only needs to have
      // been REQUESTED before the next job starts.
      task.cancel();
    }
    return generation;
  }, []);

  /**
   * Runs work on a canvas strictly after whatever was already queued for it.
   *
   * ONE JOB PER CANVAS AT A TIME, ALWAYS. This is what actually makes the collision impossible;
   * every previous attempt tried to detect the collision and lost a race doing it. pdf.js refuses
   * two concurrent renders into one canvas, so the fix is a structure where two cannot exist rather
   * than a check that hopes to notice.
   */
  const queue = useCallback((canvas: HTMLCanvasElement, job: () => Promise<void>): Promise<void> => {
    const previous = queuesRef.current.get(canvas) ?? Promise.resolve();
    // `job` on both settlements: a failed render must not stop the canvas being used again.
    const next = previous.then(job, job);
    queuesRef.current.set(canvas, next);
    void next.catch(() => {}).then(() => {
      if (queuesRef.current.get(canvas) === next) queuesRef.current.delete(canvas);
    });
    return next;
  }, []);

  /** Draws one page into whichever canvas is holding its place, and marks it if it is the quoted one. */
  const drawPage = useCallback(
    (pageNumber: number, canvas: HTMLCanvasElement, width: number): Promise<void> => {
      const doc = docRef.current;
      if (!doc || pageNumber < 1 || pageNumber > doc.numPages) return Promise.resolve();

      const generation = claim(canvas);
      const mine = () => generationRef.current.get(canvas) === generation;

      return queue(canvas, async () => {
        // Superseded while waiting its turn — a page scrolled away, or a newer quote arrived.
        if (!mine()) return;

        const pdfjs = await loadPdfjs();
        if (!mine()) return;
        const pdfPage = await doc.getPage(pageNumber);
        if (!mine()) return;

        const base = pdfPage.getViewport({ scale: 1 });
        const viewport = pdfPage.getViewport({ scale: width / base.width });
        const ratio = window.devicePixelRatio || 1;

        canvas.width = Math.floor(viewport.width * ratio);
        canvas.height = Math.floor(viewport.height * ratio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        const context = canvas.getContext("2d");
        if (!context) return;
        context.setTransform(ratio, 0, 0, ratio, 0, 0);

        const task = pdfPage.render({ canvasContext: context, viewport });
        tasksRef.current.set(canvas, task);
        try {
          await task.promise;
        } catch (err) {
          // Cancelled is the ordinary case — a page scrolled out of reach, or a newer page took the
          // canvas. Anything else is a real failure and belongs to the caller.
          if ((err as { name?: string })?.name === "RenderingCancelledException") return;
          throw err;
        } finally {
          if (tasksRef.current.get(canvas) === task) tasksRef.current.delete(canvas);
        }

        if (!mine()) return;
        if (!quote || !quote.trim() || pageNumber !== page) return;

        const content = await pdfPage.getTextContent();
        if (!mine()) return;
        const found = locateQuote(pdfjs, content.items, viewport, quote);

        // The text layer first, because when it has the answer the answer is word-accurate. The
        // annotations are the fallback, and on a CAD sheet they are almost always the only source.
        if (found.length > 0) {
          setMarks({ page: pageNumber, rects: found });
        } else if (searchAnnotations) {
          const annotations = await pdfPage.getAnnotations();
          if (!mine()) return;
          setMarks({ page: pageNumber, rects: locateInAnnotations(annotations, viewport, quote) });
        }
      });
    },
    [page, quote, searchAnnotations, claim, queue],
  );

  /**
   * Zooms, keeping the reader where they were.
   *
   * Every page's height scales with the zoom, so the vertical position that shows the same part of
   * the same sheet is the old one times the ratio — exactly, not approximately. Horizontally the
   * anchor is the CENTRE of the view rather than its left edge, because zooming in on a drawing is
   * an act of looking closer at the middle of what you can see.
   */
  const applyZoom = useCallback(
    (next: number) => {
      const wrap = wrapRef.current;
      const clamped = Math.round(Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, next)) * 100) / 100;
      if (!wrap || clamped === zoom) return;
      const ratio = clamped / zoom;
      anchorRef.current = {
        top: wrap.scrollTop * ratio,
        left: (wrap.scrollLeft + wrap.clientWidth / 2) * ratio - wrap.clientWidth / 2,
      };
      setZoom(clamped);
    },
    [zoom],
  );

  /**
   * After a zoom: put the scroll where it belongs, and redraw at the new size.
   *
   * Clearing the drawn set is what forces the redraw — a page's canvas has not changed, only how
   * many pixels belong in it, and that is the one thing syncWindow's comparison cannot see.
   */
  useLayoutEffect(() => {
    const wrap = wrapRef.current;
    const anchor = anchorRef.current;
    if (!wrap) return;
    drawnRef.current.clear();
    if (!anchor) return;
    anchorRef.current = null;
    wrap.scrollTop = Math.max(0, anchor.top);
    wrap.scrollLeft = Math.max(0, anchor.left);
  }, [zoom, slots]);

  /** Ctrl/⌘ + wheel is what a reader's hands already do. */
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    const onWheel = (e: WheelEvent) => {
      if (!e.ctrlKey && !e.metaKey) return;
      // Without this the browser zooms the whole application instead.
      e.preventDefault();
      applyZoom(zoom * (e.deltaY < 0 ? 1.12 : 1 / 1.12));
    };
    wrap.addEventListener("wheel", onWheel, { passive: false });
    return () => wrap.removeEventListener("wheel", onWheel);
  }, [applyZoom, zoom]);

  /**
   * Drag the document about.
   *
   * Tracked on the window rather than the pane: a drag that began on the page and left it mid-gesture
   * would otherwise stick, and the reader would find the document following the cursor after they had
   * let go.
   */
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      const wrap = wrapRef.current;
      if (!drag || !wrap) return;
      wrap.scrollLeft = drag.left - (e.clientX - drag.x);
      wrap.scrollTop = drag.top - (e.clientY - drag.y);
    };
    const onUp = () => {
      if (!dragRef.current) return;
      dragRef.current = null;
      wrapRef.current?.classList.remove("vr-pdf-grabbing");
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, []);

  const startDrag = (e: ReactMouseEvent) => {
    const wrap = wrapRef.current;
    // Left button only, and only where there is somewhere to drag to.
    if (!wrap || e.button !== 0) return;
    if (wrap.scrollWidth <= wrap.clientWidth && wrap.scrollHeight <= wrap.clientHeight) return;
    dragRef.current = { x: e.clientX, y: e.clientY, left: wrap.scrollLeft, top: wrap.scrollTop };
    wrap.classList.add("vr-pdf-grabbing");
  };

  /** Gives a page's pixels back, once nothing is drawing into them. */
  const releasePage = useCallback(
    (canvas: HTMLCanvasElement) => {
      claim(canvas);
      void queue(canvas, async () => {
        canvas.width = 0;
        canvas.height = 0;
      });
    },
    [claim, queue],
  );

  // ─── Paged mode ───────────────────────────────────────────────────────────────

  useEffect(() => {
    if (continuous) return;
    if (page >= 1) setCurrent(page);
  }, [page, jumpKey, continuous]);

  const renderSingle = useCallback(
    async (pageNumber: number) => {
      const canvas = canvasRef.current;
      const wrap = wrapRef.current;
      if (!docRef.current || !canvas || !wrap) return;

      const token = ++tokenRef.current;
      setRendering(true);
      setMarks({ page: 0, rects: [] });

      try {
        await drawPage(pageNumber, canvas, renderWidth || Math.max(320, wrap.clientWidth - 32));
      } catch (err) {
        if (token === tokenRef.current) {
          console.error("[verity] page render failed:", err);
          setError("That page could not be drawn.");
        }
      } finally {
        if (token === tokenRef.current) setRendering(false);
      }
    },
    [drawPage, renderWidth],
  );

  useEffect(() => {
    if (continuous) return;
    if (pageCount > 0) void renderSingle(current);
  }, [continuous, current, pageCount, renderSingle]);

  // ─── Continuous mode ──────────────────────────────────────────────────────────

  /**
   * Draw what is near the viewport, release what is not.
   *
   * Releasing means setting the bitmap to nothing while the wrapper keeps its size, so the layout
   * never moves and the scrollbar never lies. Without it, scrolling a thirty-sheet pack end to end
   * leaves thirty canvases live and the tab holding well over a hundred megabytes of pixels for the
   * one sheet in front of the reader.
   */
  const syncWindow = useCallback(() => {
    const wrap = wrapRef.current;
    if (!continuous || !wrap || slots.length === 0) return;

    const from = wrap.scrollTop - RENDER_MARGIN;
    const to = wrap.scrollTop + wrap.clientHeight + RENDER_MARGIN;

    slots.forEach((slot, index) => {
      const pageNumber = index + 1;
      const canvas = canvasesRef.current.get(pageNumber);
      if (!canvas) return;

      const near = slot.top + slot.height >= from && slot.top <= to;

      if (near && drawnRef.current.get(pageNumber) !== canvas) {
        drawnRef.current.set(pageNumber, canvas);
        void drawPage(pageNumber, canvas, slot.width).catch((err) => {
          drawnRef.current.delete(pageNumber);
          console.error("[verity] page render failed:", err);
        });
      } else if (!near && drawnRef.current.has(pageNumber)) {
        drawnRef.current.delete(pageNumber);
        // Queued, not immediate. Zeroing a canvas that pdf.js is still drawing into leaves the task
        // alive, and scrolling back then starts a second render into the same canvas.
        releasePage(canvas);
      }
    });

    // The page the reader is looking at is the last one whose top has passed the fold.
    const at = wrap.scrollTop + 80;
    let seen = 1;
    for (let i = 0; i < slots.length; i++) if (slots[i]!.top <= at) seen = i + 1;
    setCurrent(seen);
  }, [continuous, slots, drawPage, releasePage]);

  /** Scroll events are frequent; the work is not. */
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!continuous || !wrap) return;

    let queued = 0;
    const onScroll = () => {
      if (queued) return;
      queued = requestAnimationFrame(() => {
        queued = 0;
        syncWindow();
      });
    };

    wrap.addEventListener("scroll", onScroll, { passive: true });
    syncWindow();
    return () => {
      wrap.removeEventListener("scroll", onScroll);
      if (queued) cancelAnimationFrame(queued);
    };
  }, [continuous, syncWindow]);

  /**
   * Take the stack to the page the caller asked for.
   *
   * `jumpKey` is in the dependencies so that asking for the same page twice scrolls there twice —
   * a reviewer who has scrolled away and clicks the same entity again means "take me back".
   */
  useEffect(() => {
    if (!continuous) return;
    const wrap = wrapRef.current;
    const slot = slots[page - 1];
    // Not yet laid out. Deliberately without recording the jump, so it happens once the stack has
    // measured itself rather than being lost because the document had not opened in time.
    if (!wrap || !slot) return;

    // `slots` is rebuilt whenever the pane is resized, and without this that alone would count as a
    // jump — dragging the window edge would snatch the reader back to the last entity they clicked.
    const signature = `${page}:${jumpKey}`;
    if (jumpedRef.current === signature) return;
    jumpedRef.current = signature;

    // INSTANT, NOT SMOOTH. A smooth scroll from sheet 1 to sheet 28 drags the render window across
    // every sheet between them, drawing and releasing two dozen CAD pages to arrive at one. The
    // prev/next buttons still glide, because there the journey is a page long.
    wrap.scrollTo({ top: slot.top, behavior: "auto" });
    // A smooth scroll fires its own events, but the target page should be drawn before it arrives
    // rather than after.
    syncWindow();
  }, [continuous, page, jumpKey, slots, syncWindow]);

  /** A changed quote re-marks the page it belongs to without redrawing the whole stack. */
  useEffect(() => {
    if (!continuous) return;
    setMarks({ page: 0, rects: [] });
    const canvas = canvasesRef.current.get(page);
    if (canvas && drawnRef.current.has(page)) {
      void drawPage(page, canvas, slots[page - 1]?.width ?? paneWidth);
    }
  }, [continuous, quote, jumpKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const goTo = (pageNumber: number) => {
    const next = Math.min(Math.max(1, pageNumber), pageCount || 1);
    if (!continuous) {
      setCurrent(next);
      return;
    }
    const wrap = wrapRef.current;
    const slot = slots[next - 1];
    if (wrap && slot) wrap.scrollTo({ top: slot.top, behavior: "smooth" });
  };

  // Three different nothings, and only one of them is "nothing to show": the document is being
  // fetched, the document is here but pdf.js has not opened it yet, or there genuinely isn't one.
  if (!file) {
    return (
      <div className="vr-pdf-pane vr-pdf-empty">
        {loading ? (
          <div className="vr-pdf-loading-box">
            <p className="vr-pdf-loading">
              <Loader2 size={15} className="vr-spin-inline" /> {loadingLabel}
              {typeof loadingProgress === "number" && (
                <span className="vr-pdf-loading-pct">{Math.round(loadingProgress * 100)}%</span>
              )}
            </p>
            {/* Stops at 99 by design — the bytes are in but the document is not open yet, and a bar
                sitting full while nothing happens is the one thing worse than no bar. The pane's own
                "Reading…" takes over from there. */}
            {typeof loadingProgress === "number" && (
              <span className="vr-pdf-loading-track">
                <span
                  className="vr-pdf-loading-fill"
                  style={{ width: `${Math.round(loadingProgress * 100)}%` }}
                />
              </span>
            )}
          </div>
        ) : (
          <p>No document open.</p>
        )}
      </div>
    );
  }

  // A source document is large — a standard is 27 MB, a drawing pack 35 — so pdf.js takes a moment
  // to parse it before there is a page count or a first page to draw. Saying so beats a blank canvas.
  const opening = (pageCount === 0 || (continuous && slots.length === 0)) && !error;

  return (
    <div className="vr-pdf-pane">
      <div className="vr-pdf-bar">
        <button
          className="vr-rowbtn"
          onClick={() => goTo(current - 1)}
          disabled={current <= 1}
          aria-label="Previous page"
        >
          <ChevronLeft size={13} />
        </button>
        <span className="vr-pdf-page">
          Page {current}
          {pageCount > 0 && <span className="vr-cell-dim"> of {pageCount}</span>}
        </span>
        <button
          className="vr-rowbtn"
          onClick={() => goTo(current + 1)}
          disabled={pageCount > 0 && current >= pageCount}
          aria-label="Next page"
        >
          <ChevronRight size={13} />
        </button>
        {rendering && <Loader2 size={13} className="vr-spin-inline vr-cell-dim" />}

        {/* Zoom redraws the page rather than stretching it, so the percentage is how much detail is
            on screen and not how far a picture has been blown up. */}
        <span className="vr-pdf-zoom">
          <button
            className="vr-rowbtn"
            onClick={() => applyZoom(zoom / 1.25)}
            disabled={zoom <= MIN_ZOOM}
            aria-label="Zoom out"
          >
            <ZoomOut size={13} />
          </button>
          <button
            className="vr-pdf-zoom-num"
            onClick={() => applyZoom(1)}
            disabled={zoom === 1}
            title="Fit to the pane"
          >
            {Math.round(zoom * 100)}%
          </button>
          <button
            className="vr-rowbtn"
            onClick={() => applyZoom(zoom * 1.25)}
            disabled={zoom >= MAX_ZOOM}
            aria-label="Zoom in"
          >
            <ZoomIn size={13} />
          </button>
        </span>

        <span className="vr-pdf-name vr-cell-dim">{file.name}</span>
      </div>

      <div
        className={`vr-pdf-scroll${continuous ? " vr-pdf-stack" : ""}${zoom > 1 ? " vr-pdf-pannable" : ""}`}
        ref={wrapRef}
        onMouseDown={startDrag}
      >
        {error ? (
          <div className="vr-empty">{error}</div>
        ) : opening ? (
          <div className="vr-empty vr-pdf-loading">
            <Loader2 size={15} className="vr-spin-inline" /> Reading {file.name}…
          </div>
        ) : continuous ? (
          <div className="vr-pdf-stack-body">
            {slots.map((slot, index) => {
              const pageNumber = index + 1;
              return (
                <div
                  key={pageNumber}
                  className="vr-pdf-page-wrap"
                  style={{ width: slot.width, height: slot.height }}
                >
                  {/* THE REF MUST NOT DECIDE ANYTHING. An inline ref callback is a new function on
                      every render, so React detaches it with null and reattaches it with the very
                      same DOM node each time — dozens of times while scrolling. Treating that as
                      "the canvas went away" wiped the drawn set on every re-render, and the next
                      scroll started a fresh render into every canvas that was already drawing.
                      Whether a page needs drawing is decided in syncWindow, by comparing the canvas
                      a page was drawn into with the one now holding its place. */}
                  <canvas
                    ref={(el) => {
                      if (el) canvasesRef.current.set(pageNumber, el);
                      else canvasesRef.current.delete(pageNumber);
                    }}
                  />
                  <span className="vr-pdf-page-no">{pageNumber}</span>
                  {marks.page === pageNumber &&
                    marks.rects.map((rect, i) => (
                      <div
                        key={i}
                        className="vr-pdf-mark"
                        style={{
                          left: `${rect.left * 100}%`,
                          top: `${rect.top * 100}%`,
                          width: `${rect.width * 100}%`,
                          height: `${rect.height * 100}%`,
                        }}
                      />
                    ))}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="vr-pdf-page-wrap">
            <canvas ref={canvasRef} />
            {marks.rects.map((rect, i) => (
              <div
                key={i}
                className="vr-pdf-mark"
                style={{
                  left: `${rect.left * 100}%`,
                  top: `${rect.top * 100}%`,
                  width: `${rect.width * 100}%`,
                  height: `${rect.height * 100}%`,
                }}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * Finds a quote among a page's text runs and returns its boxes.
 *
 * pdf.js splits a line into however many runs the PDF happened to encode, so a quoted sentence is
 * almost never one item. The runs are joined into a single string with a map back to the run each
 * character came from; the quote is then located in that string and every run it touches is boxed.
 * Whitespace is collapsed on both sides first, because a quote that spans a line break is still the
 * same sentence. No match means no highlight — the page is shown unmarked rather than marked wrongly.
 */
function locateQuote(pdfjs: any, items: any[], viewport: any, quote: string): Rect[] {
  let joined = "";
  // For every character in `joined`, which item it came from.
  const owner: number[] = [];

  items.forEach((item, index) => {
    const text = typeof item.str === "string" ? item.str : "";
    if (!text) return;
    for (const ch of text) {
      joined += ch;
      owner.push(index);
    }
    // pdf.js marks the end of a line; treat it as a space so runs do not fuse into one word.
    if (item.hasEOL) {
      joined += " ";
      owner.push(index);
    }
  });

  // Collapse whitespace, keeping a map from the collapsed string back to the original positions.
  let flat = "";
  const flatToOwner: number[] = [];
  let lastWasSpace = true;
  for (let i = 0; i < joined.length; i++) {
    const ch = joined[i]!;
    if (/\s/.test(ch)) {
      if (lastWasSpace) continue;
      flat += " ";
      flatToOwner.push(owner[i]!);
      lastWasSpace = true;
    } else {
      flat += ch;
      flatToOwner.push(owner[i]!);
      lastWasSpace = false;
    }
  }

  const needle = quote.replace(/\s+/g, " ").trim();
  if (!needle) return [];

  const at = flat.indexOf(needle);
  if (at < 0) return [];

  const touched = new Set<number>();
  for (let i = at; i < Math.min(at + needle.length, flatToOwner.length); i++) {
    touched.add(flatToOwner[i]!);
  }

  const rects: Rect[] = [];
  for (const index of touched) {
    const item = items[index];
    if (!item) continue;

    // Item space to device space, then to fractions of the rendered page.
    const tx = pdfjs.Util.transform(viewport.transform, item.transform);
    const fontHeight = Math.hypot(tx[2], tx[3]);
    const width = (item.width ?? 0) * viewport.scale;
    if (width <= 0 || fontHeight <= 0) continue;

    rects.push({
      left: tx[4] / viewport.width,
      top: (tx[5] - fontHeight) / viewport.height,
      width: width / viewport.width,
      height: fontHeight / viewport.height,
    });
  }

  return rects;
}

/**
 * Finds a quote among a page's ANNOTATIONS and returns their boxes.
 *
 * This is how a value gets found on a CAD sheet. AutoCAD SHX text plots as vector outlines, and the
 * plot driver captures each string as a `Square` annotation whose `contentsObj.str` holds the real
 * text — so on the reference pack page 15 carries 477 characters in its text layer and 22,488 in its
 * annotations. The reader that produced these entities works the same way, which is why a value it
 * reported is reliably findable here.
 *
 * THE BOX IS THE ANNOTATION'S, AND THAT IS COARSER THAN A WORD. One MTEXT is one annotation, so a
 * standalone label gives a tight box while a value inside a general-notes block gives the block. It
 * lands the eye in the right place on a 34x22 sheet, which is the job; it is not an underline, and
 * pretending otherwise would mean measuring glyphs pdf.js never positioned.
 *
 * The capture also repeats itself — the first wrapped line of each paragraph appears twice, sometimes
 * glued without a space — but a substring search is indifferent to that, so no de-duplication is
 * needed here the way it is in the reader.
 */
function locateInAnnotations(annotations: any[], viewport: any, quote: string): Rect[] {
  const needle = quote.replace(/\s+/g, " ").trim().toLowerCase();
  if (!needle) return [];

  const rects: Rect[] = [];
  for (const annotation of annotations) {
    const text: string =
      annotation?.contentsObj?.str ?? (typeof annotation?.contents === "string" ? annotation.contents : "");
    if (!text) continue;
    if (!text.replace(/\s+/g, " ").toLowerCase().includes(needle)) continue;

    const rect = annotation.rect;
    if (!rect || rect.length < 4) continue;

    // Annotation rects are in PDF user space with the origin bottom-left; the viewport knows how to
    // put them where the page was actually drawn, at whatever scale it was drawn at.
    const [x1, y1, x2, y2] = viewport.convertToViewportRectangle(rect);
    const left = Math.min(x1, x2);
    const top = Math.min(y1, y2);
    const width = Math.abs(x2 - x1);
    const height = Math.abs(y2 - y1);
    if (width <= 0 || height <= 0) continue;

    rects.push({
      left: left / viewport.width,
      top: top / viewport.height,
      width: width / viewport.width,
      height: height / viewport.height,
    });

    // A term like "OPSD" can appear in twenty blocks on one sheet, and twenty highlights mark
    // nothing. The first is the one the eye needs.
    if (rects.length >= 4) break;
  }

  return rects;
}

export default PdfPane;
